import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, dispatch } from 'src/redux/store';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { SUB_TAB_TO_PARENT_TABLE, TASKS_V2_CHANGED_EVENT } from 'src/constants/tasksV2';
import { _activityCache } from 'src/sections/tasks/useDeliveryStatus';
import { initializeWhatsapp } from 'src/redux/slices/whatsappV2';

/**
 * Hook that fetches task checklist columns for a given table.
 * Returns column definitions that can be merged into the DataGrid columns.
 *
 * @param {number|string} practiceId - Practice ID
 * @param {number[]} leadIds - Array of lead IDs currently visible in the table
 * @param {string} tableTitle - The table title used to filter which checklist items appear (e.g. 'New Leads')
 * @param {string} [activeSubTab] - Active sub-tab value (e.g. "2", "NHS") — passed as &sub_tab= so BE filters by applies_to_sub_tab
 * @param {string} [activeCustomTabSlug] - Slug of the active custom tab — when set, BE uses checklist_binding resolution instead of show_in_tables
 * @param {boolean} [disabled] - When true, skip the fetch entirely and return empty columns. Used by the "All" parent view of tables that have custom checklist-bound tabs alongside it (e.g. ConsultantAttend on practice 92): the All view should aggregate leads but NOT inherit checklist columns from `show_in_tables`, which would otherwise leak the per-custom-tab checklist UI onto the unfiltered list.
 * @returns {{ columns: Array, data: Object, loading: boolean, error: boolean, refresh: Function }}
 */
export default function useTaskChecklistColumns(practiceId, leadIds, tableTitle, activeSubTab, activeCustomTabSlug, disabled = false) {
    const [columns, setColumns] = useState([]);
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    // Refs mirroring the latest columns/data so the fetch callback can decide
    // whether to clear (first-load) or preserve (transient refetch failure)
    // without re-binding on every state change.
    const columnsRef = useRef(columns);
    columnsRef.current = columns;
    const dataRef = useRef(data);
    dataRef.current = data;

    // Auto-retry timer for transient failures. Cleared on cancellation
    // (params change / unmount) via the existing fetchGenRef bump.
    const retryTimerRef = useRef(null);

    const waBusinessId = useSelector((s) => s.whatsappV2.currentBusinessId);
    const waLoading = useSelector((s) => s.whatsappV2.isLoading);
    const waNotConfigured = useSelector((s) => s.whatsappV2.whatsappNotConfigured);

    // Pre-init WhatsApp so fetchWhatsappChatAndOtherMetaData's business/phone ID guard
    // doesn't bail when a WA cell is opened from this table context.
    useEffect(() => {
        if (practiceId && (waBusinessId === -1) && !waLoading && !waNotConfigured) {
            dispatch(initializeWhatsapp());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId]);

    const numericIds = Array.isArray(leadIds)
        ? leadIds.map((id) => parseInt(id, 10)).filter((id) => !Number.isNaN(id))
        : [];
    const sortedIdsStr = [...numericIds].sort((a, b) => a - b).join(',');

    // Generation counter: incremented on each new fetch. Each fetch closes over its
    // own generation value and discards the response if a newer fetch has started.
    // This avoids the stale-closure problem where a shared cancelledRef gets reset
    // by the new effect before the previous in-flight response resolves.
    const fetchGenRef = useRef(0);

    // Stash every fetch input in a ref kept fresh each render. fetchColumns reads
    // from these refs at invoke time, so a single stable callback identity always
    // produces a request with the LATEST lead_ids / sub_tab / table / custom_tab.
    // Without this, a `refresh` reference captured by a long-lived consumer
    // (slideout, composer dialog, overflow popover) holds a closure built when
    // the parent table held a different lead set, producing requests like
    //   /tasks/table-columns/?lead_ids=<old-batch>&sub_tab=<new-tab>
    // which return data for the wrong batch and leave the clicked row's cells
    // un-refreshed. Mirrors the ChoicesField pattern that uses reloadCounter /
    // updateLeadPage as stable references that read current Redux state.
    const sortedIdsStrRef = useRef(sortedIdsStr);
    sortedIdsStrRef.current = sortedIdsStr;
    const tableTitleRef = useRef(tableTitle);
    tableTitleRef.current = tableTitle;
    const activeSubTabRef = useRef(activeSubTab);
    activeSubTabRef.current = activeSubTab;
    const activeCustomTabSlugRef = useRef(activeCustomTabSlug);
    activeCustomTabSlugRef.current = activeCustomTabSlug;

    // Forward ref so the failure paths inside fetchColumns can schedule a retry
    // that calls the latest fetchColumns without depending on its identity.
    const fetchColumnsRef = useRef(null);

    const scheduleRetry = (gen) => {
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
        }
        retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            // Bail if a newer fetch already ran (params changed / unmounted).
            if (gen !== fetchGenRef.current) return;
            if (fetchColumnsRef.current) fetchColumnsRef.current();
        }, 3000);
    };

    const fetchColumns = useCallback(async () => {
        if (!practiceId) {
            setColumns([]);
            setData({});
            return;
        }
        // Explicit caller-side suppression — e.g. the "All" parent view on tables
        // with checklist-bound custom tabs siblings (Risk 2 / task #100 follow-up).
        // Clear any prior state so columns from a previous tab don't linger.
        if (disabled) {
            setColumns([]);
            setData({});
            return;
        }
        // Pass practiceId so we don't race a stale V2 permissions snapshot during
        // a practice switch into a V1 practice — the snapshot may still belong to
        // the previous (V2) practice while `practiceId` already reflects the new
        // (V1) one, causing a 403 storm on /tasks/table-columns/ (Sentry
        // DENTALSEM-CRM-1D9K, ~12.8k events / 11h).
        if (!hasFeatureAccess('tasks-v2', practiceId)) {
            setColumns([]);
            setData({});
            return;
        }

        fetchGenRef.current += 1;
        const gen = fetchGenRef.current;

        // Any pending retry from a previous failure is superseded by this fetch.
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }

        setLoading(true);
        setError(false);
        try {
            const currentTableTitle = tableTitleRef.current;
            const canonicalTable = SUB_TAB_TO_PARENT_TABLE[currentTableTitle] || currentTableTitle;
            const currentCustomTabSlug = activeCustomTabSlugRef.current;
            const hasCustomTab = currentCustomTabSlug !== undefined
                && currentCustomTabSlug !== null
                && currentCustomTabSlug !== '';

            // Skip the fetch when no leads are loaded yet — without lead_ids the BE
            // returns columns but zero cell data, and the resulting empty state can
            // overwrite a prior good result during a lead-list refetch race.
            if (!sortedIdsStrRef.current) {
                if (gen === fetchGenRef.current) {
                    setLoading(false);
                }
                return;
            }

            const params = new URLSearchParams();
            params.set('lead_ids', sortedIdsStrRef.current);
            if (canonicalTable) params.set('table', canonicalTable);
            const currentSubTab = activeSubTabRef.current;
            // When a custom_tab is active, BE resolves columns via checklist_binding
            // for that tab — passing sub_tab="All" alongside conflicts with that path
            // and yields empty columns. Custom tab wins.
            if (!hasCustomTab && currentSubTab !== undefined && currentSubTab !== null && currentSubTab !== '') {
                params.set('sub_tab', currentSubTab);
            }
            if (hasCustomTab) {
                params.set('custom_tab', currentCustomTabSlug);
            }

            const response = await callApi(
                `/api/v1/practices/${practiceId}/tasks/table-columns/?${params.toString()}`
            );

            if (gen !== fetchGenRef.current) return;

            if (response.ok && response.data) {
                setColumns(response.data.columns || []);
                setData(response.data.data || {});
            } else {
                // Transient refetch failure: keep last good columns/data so the
                // row's cells don't blank out (e.g. a click that triggered a
                // /complete/ POST followed by a 5xx on the refresh). Only clear
                // when nothing has rendered yet so the empty state can show.
                const hasExistingColumns = (columnsRef.current || []).length > 0;
                const hasExistingData = Object.keys(dataRef.current || {}).length > 0;
                if (!hasExistingColumns && !hasExistingData) {
                    setColumns([]);
                    setData({});
                }
                setError(true);
                scheduleRetry(gen);
            }
        } catch (err) {
            if (gen !== fetchGenRef.current) return;
            const hasExistingColumns = (columnsRef.current || []).length > 0;
            const hasExistingData = Object.keys(dataRef.current || {}).length > 0;
            if (!hasExistingColumns && !hasExistingData) {
                setColumns([]);
                setData({});
            }
            setError(true);
            scheduleRetry(gen);
        } finally {
            if (gen === fetchGenRef.current) setLoading(false);
        }
    // Only practiceId and disabled in deps — everything else is read from refs at invoke time.
    // This keeps fetchColumns identity stable so captured `refresh` references in
    // long-lived components never go stale on the params we read here.
    // `disabled` is included because toggling it must re-run the early-return branch
    // and clear/restore state immediately rather than waiting for another param change.
    }, [practiceId, disabled]);

    fetchColumnsRef.current = fetchColumns;

    // Debounced refresh — coalesces calls within a 200ms window so a burst of
    // rapid cell clicks (5 statuses in <1s) collapses into a single GET
    // /tasks/table-columns/ instead of N parallel requests. Each new call
    // resets the timer, and a trailing fetch fires once activity stops.
    const debounceTimerRef = useRef(null);
    const debouncedRefresh = useCallback(() => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null;
            if (fetchColumnsRef.current) fetchColumnsRef.current();
        }, 200);
    }, []);

    useEffect(() => () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    }, []);

    // Auto-refetch whenever any input changes. fetchColumns itself is stable (deps
    // only on practiceId), so we trigger the refetch on the params explicitly.
    // Debounced 150ms so rapid lead-list changes (mount → treatment-filter settle)
    // coalesce into a single request rather than firing two back-to-back.
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchColumns();
        }, 150);
        // Invalidate any in-flight fetch from the previous render on cleanup,
        // and drop any scheduled auto-retry so it doesn't fire against stale params.
        return () => {
            clearTimeout(timer);
            fetchGenRef.current += 1;
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId, sortedIdsStr, tableTitle, activeSubTab, activeCustomTabSlug, disabled]);

    // Window focus: invalidate activity cache so delivery badges refresh when user returns to tab
    useEffect(() => {
        const handleFocus = () => {
            Object.keys(_activityCache).forEach((key) => delete _activityCache[key]);
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, []);

    // Cross-component refresh: the guided-checklist popup (and any other tasks-v2
    // surface) dispatches TASKS_V2_CHANGED_EVENT on window after a mutation. The popup
    // is owned by the V1 selectedRow flow and can't reach this hook's refresh directly,
    // so we listen here and refetch the per-task columns — fixes the "table date cell
    // stays stale after a popup skip/complete/pause" bug without prop-drilling.
    useEffect(() => {
        const handleTasksChanged = () => { debouncedRefresh(); };
        window.addEventListener(TASKS_V2_CHANGED_EVENT, handleTasksChanged);
        return () => window.removeEventListener(TASKS_V2_CHANGED_EVENT, handleTasksChanged);
    }, [debouncedRefresh]);

    return { columns, data, loading, error, refresh: debouncedRefresh };
}
